import {HttpBackend, HttpClient} from '@angular/common/http';
import {EventEmitter, Injectable} from '@angular/core';
import * as HttpStatusCode from 'http-status-codes';
import {filter, retry, tap} from 'rxjs/operators';
import {environment} from '../../../../environments/environment';
import {User} from '../../models/fusion-auth';
import {generateGravatarUrl, HttpRequestBuilder} from '../../utils';
import {StorageService} from '../general';
import {BehaviorSubject} from "rxjs";

@Injectable({
  providedIn: 'root',
})
export class FusionAuthService {
  public applicationId: string;
  public baseUrl: string;
  private profileSubject = new BehaviorSubject<User>(this.Profile);


  constructor(
    private storage: StorageService,
    public handler: HttpBackend,
    private http: HttpClient
  ) {
    this.applicationId = environment.fusionAuth.applicationId;
    this.baseUrl = environment.fusionAuth.apiUrl;
  }

  public get IsLoggedIn() {
    return this.storage.get('isLoggedIn', 'fusionAuth');
  }

  public get Profile(): User {
    const value = this.storage.get('user', 'auth');
    return JSON.parse(value ? value : '{}');
  }

  public get Profile$() {
    return this.profileSubject.asObservable().pipe(filter((x): x is User => !!x));
  }

  public getToken(): string {
    const value = this.storage.get('token', 'auth');
    return value ? value : '';
  }

  public set SignatureImage(image: string) {
    const value = this.storage.get('user', 'auth');
    const user = JSON.parse(value ? value : '{}');
    user.data = {
      ...user.data,
        signImage: image
    }

    this.storage.store(JSON.stringify(user), 'user', 'auth');
    this.profileSubject.next(user);
  }

  public getRefreshToken(): string {
    const value = this.storage.get('refreshToken', 'auth');
    return value ? value : '';
  }

  public changePassword(request: any) {
    return this.start()
      .setUri('/user/change-password')
      .setJsonBody(request)
      .setMethod('POST')
      .build();
  }

  public exchangeRefreshTokenForJWT() {
    const request = {
      refreshToken: this.getRefreshToken(),
    };

    return this.start()
      .setUri('/jwt/refresh')
      .setJsonBody(request)
      .setMethod('POST')
      .setHeader('X-FusionAuth-TenantId', environment.fusionAuth.tenantId)
      .build()
      .pipe(
        retry(2),
        tap((response: any) => {
          if (response.status === HttpStatusCode.OK) {
            this.storage.store(response.body.token, 'token', 'auth');
          }
        }),
      );
  }

  public cleanCredentials() {
    this.storage.remove('token', 'auth');
    this.storage.remove('user', 'auth');
    this.storage.remove('refreshToken', 'auth');
    this.setLoggedIn(false);
  }

  public forgotPassword(request: any) {
    request.applicationId = this.applicationId;

    return this.start()
      .setHeader('X-FusionAuth-TenantId', environment.fusionAuth.tenantId)
      .setUri('/user/forgot-password')
      .setJsonBody(request)
      .setMethod('POST')
      .build();
  }

  public login(request: any, skipInterceptor?: boolean, withCredentials: boolean = true) {
    request = {...request, applicationId: this.applicationId};

    return this.start(skipInterceptor)
      .setHeader('authorization', environment.fusionAuth.apiKey)
      .setHeader('Access-Control-Allow-Origin', '*')
      .setHeader('Content-Type', 'text/plain')
      .setUri('/login')
      .setJsonBody(request)
      .setMethod('POST')
      .build(withCredentials)
      .pipe(
        tap((response: any) => {
          if (response.body.user.imageUrl === null || response.body.user.imageUrl === undefined) {
            response.body.user.imageUrl = generateGravatarUrl(response.body.user.email);
          }
        }),
        tap((response: any) => {
          if (response.status === HttpStatusCode.OK) {
            this.storage.store(JSON.stringify(response.body.user), 'user', 'auth');
            this.storage.store(response.body.token, 'token', 'auth');
            this.storage.store(response.body.refreshToken, 'refreshToken', 'auth');
            this.storage.store(String(false), 'logged', 'fusionAuth');
            this.setLoggedIn(true);
          }
        }),
      );
  }

  public hasBeenLogged() {
    return this.storage.get('logged', 'fusionAuth');
  }

  public clearHasBeenLogged() {
    return this.storage.store(String(true), 'logged', 'fusionAuth');
  }

  public setLoggedIn(value: boolean) {
    return this.storage.store(String(value), 'isLoggedIn', 'fusionAuth');
  }

  public logout(globalLogout?: any, refreshToken?: string) {
    return this.start()
      .setHeader('Content-Type', 'text/plain')
      .setUri('/logout')
      .setUrlParameter('global', globalLogout)
      .setUrlParameter('refreshToken', refreshToken)
      .setMethod('POST')
      .build()
      .pipe(
        tap(() => {
          this.cleanCredentials();
        }),
      );
  }

  private start(skipInter?: boolean) {
    const httpRequestBuilder = skipInter ?
      new HttpRequestBuilder(new HttpClient(this.handler)).setUrl(this.baseUrl) :
      new HttpRequestBuilder(this.http).setUrl(this.baseUrl);
    httpRequestBuilder.getOptions().reportProgress = true;
    return httpRequestBuilder;
  }

  public validateJWT() {
    return this.start()
      .setHeader('Content-Type', 'text/plain')
      .setUri('/jwt/validate')
      .setMethod('GET')
      .build().pipe(retry(2));
  }
}
